import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CoreConstants, DefaultAccessModel } from '../core.constants';

const profileImagePath = 'assets/images/user/user-profile.png';

export class UserProfilePojo {
  profilePicture = profileImagePath;
  firstName: string;
  lastName: string;
  roles: string[];
  allowedUiPages: string[] = [];
  allowedUiMenus: string[] = [];
  emailId: string;
  phoneNumber?: string;
  crmId: string;
  organisations: UserDetailsOrganisation[] = [];
  permissions: PermissionModel[] = [];
  // Set on UI side
  internalStaff?: boolean;
  appRoles?: string[];
  orgRoles?: string[];
  appPermissions?: PermissionModel[];
  orgPermissions?: PermissionModel[];
  additionalAttributes: ProfileAdditionalAttributes;
}

export class ProfileAdditionalAttributes {
  customerSignUp: boolean;
  penTesterSignUp: boolean;
  workSpaceSignup: boolean;
  changeOwnerSignUp: boolean;
  onBoardingDone: boolean;
  caVerificationDone: boolean;
  verificationRequestRejected: boolean;
  location: string;
  orgName: string;
  caOnBoardRole: string;
  wpOnBoardRole: string;
  pentesterAlias: string;
  skills: string[];
  certifications: string[];
  profileHeadline: string;
  ptExperience: string;
  socTwitter: string;
  socGoogle: string;
  socLinkedIn: string;
  penTestTypes: string[];
  eula: boolean;
  joiningExistingOrg: boolean;
  resumeDocId: string;
  introVideoDocId: string;
  workspaces: any[];
  organizationBlocked: boolean;
  introVideo: {
    documentId: string,
    documentUrl: string,
    fileName: string;
  };
  resume: {
    documentId: string;
    fileName: string;
  };
  introVideoType: string;
  introVideoUrl: string;
  companyNameOnBoard: string;
  companyActive: boolean;
  companyName: string;
  companyId: string;
}

export class PermissionModel {
  id: string;
  name: string;
  permissionKey: string;
  menuId: string;
  uiOperations: DefaultAccessModel;
  uiPages: string[];
  apis: string[];
}

export class UIOperationModel {
  apis: string[];
  key: string;
  name: string;

  constructor() {
    this.apis = [];
  }
}

export interface UserDetailsOrganisation {
  id: string;
  name: string;
  alias: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {

  public Observable: BehaviorSubject<UserProfilePojo> = new BehaviorSubject<UserProfilePojo>(null);
  private userData: UserProfilePojo = new UserProfilePojo();

  constructor() {
    this.Observable.next(new UserProfilePojo());
  }

  setUserDetails(data: UserProfilePojo): void {
    const keys = Object.keys(data || {});
    this.userData.profilePicture = profileImagePath;
    keys.forEach((key) => {
      if (data.hasOwnProperty(key)) {
        this.userData[key] = data[key];
      }
    });
    this.Observable.next(this.userData);
  }

  setUser(data: UserProfilePojo, setPermission: boolean = false, orgSelected: boolean = false): void {
    CoreConstants.firstPage = CoreConstants.firstPageOriginal;
    if (data) {
      data.internalStaff = false;
      if (data.appRoles && data.appRoles.length && (!data.organisations || (data.organisations && data.organisations.length === 0))) {
        data.internalStaff = true;
        CoreConstants.firstPage = CoreConstants.ADMIN_LANDING_PAGE;
      }
      const keys = Object.keys(data);
      this.userData.profilePicture = profileImagePath;
      keys.forEach((key) => {
        this.userData[key] = data[key];
      });
      if (!this.userData.appRoles) {
        this.userData.appRoles = [];
      }
      if (!this.userData.orgRoles) {
        this.userData.orgRoles = [];
      }
      if (!this.userData.orgPermissions) {
        this.userData.orgPermissions = [];
      }
      if (!this.userData.appPermissions) {
        this.userData.appPermissions = [];
      }

      if (setPermission && !orgSelected) {
        this.userData.internalStaff = true;
        // CoreConstants.firstPage = CoreConstants.ADMIN_LANDING_PAGE;
      }
      this.userData.allowedUiMenus = [...CoreConstants.allowedMenus];
      this.userData.allowedUiPages = [];
      const permissionMap = {};
      if (setPermission) {
        (orgSelected ? this.userData.orgPermissions : this.userData.appPermissions).forEach((permission: any) => {
          if (permission.permissionKey) {
            permissionMap[permission.permissionKey] = permission.uiOperations || {};
          }
          this.userData.allowedUiPages.push(...permission.uiPages || []);
          if (permission.menuId) {
            this.userData.allowedUiMenus.push(permission.menuId);
          }
        });
      }
      CoreConstants.rightMask$.next(permissionMap);
      this.Observable.next(this.userData);
    }
  }


  getObserver(): Observable<UserProfilePojo> {
    return this.Observable;
  }

  getUser(): UserProfilePojo {
    return this.userData;
  }

  setUserProperty(key: string, value: any): void {
    this.userData[key] = value;
    this.Observable.next(this.userData);
  }

  getUserProperty(key: string): any {
    return this.userData[key];
  }

  resetUser(): void {
    this.userData = new UserProfilePojo();
  }

  isPentesterUser(): boolean {
    return this.userData?.additionalAttributes?.penTesterSignUp;
  }

  isWSParticipantUser(): boolean {
    return this.userData?.additionalAttributes?.workSpaceSignup;
  }

  isCustomer(): boolean {
    return this.userData?.additionalAttributes?.customerSignUp || this.userData?.additionalAttributes?.workSpaceSignup || this.userData?.additionalAttributes?.changeOwnerSignUp;
  }

  isOrgAdmin(): boolean {
    return this.userData?.additionalAttributes?.customerSignUp || this.userData?.additionalAttributes?.changeOwnerSignUp;
  }

  isInternalUser(): boolean {
    return this.userData?.appRoles.length > 0;
  }
}
